'use client';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TradeAssetsPageSchema } from '@shared/zod-schemas/frontend';
import { useSearchParams } from 'next/navigation';
import { BuySellAssetControllerStages } from '@shared/types/frontend/assetPage';
import { convertBasicTokenInfoToCovalentBalanceItem } from '@/hooks/pricing/useBasicTokenInfo';
import { useSwap } from '@/contexts/SwapProvider';
import { useSession } from '@/contexts/LitSessionProvider';
import { useOrderHistory } from '@/hooks/db/useOrderHistory';
import useERC20Balance from '@/hooks/portfolio/useERC20Balance';
import { useSearchHome } from '@/contexts/SearchHomeProvider';
import { useTokenData } from '@/hooks/asset/useTokenData';
export const useTradeForm = () => {
  const {
    PKP
  } = useSession();
  const {
    setKeyword,
    setMode
  } = useSearchHome();
  const urlParams = useSearchParams();
  const [tokenAddress, tokenNetwork, tokenSide] = [urlParams.get('address'), urlParams.get('networkId'), urlParams.get('side')];
  const [stage, setStage] = useState(BuySellAssetControllerStages.default);
  const {
    data: tokenData
  } = useTokenData({
    tokenAddress,
    tokenNetwork
  });
  const {
    data: assetData
  } = useERC20Balance({
    evmAddresses: [PKP === null || PKP === void 0 ? void 0 : PKP.additionalEthAddress],
    solanaAddresses: [PKP === null || PKP === void 0 ? void 0 : PKP.solanaAddress],
    useGeniusAPI: true
  });
  const {
    refetch: refetchOrderHistory
  } = useOrderHistory({
    cloudWallet: PKP === null || PKP === void 0 ? void 0 : PKP.additionalEthAddress,
    solanaAddress: PKP === null || PKP === void 0 ? void 0 : PKP.solanaAddress
  });
  const {
    setFromAssets,
    setToAssets
  } = useSwap();
  const _convertTokenData = () => {
    var _a;
    const _params = {
      token: tokenData === null || tokenData === void 0 ? void 0 : tokenData.token,
      tokenInfo: (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.tokenInfo) === null || _a === void 0 ? void 0 : _a.results,
      prices: [tokenData === null || tokenData === void 0 ? void 0 : tokenData.prices]
    };
    const _convertedAsset = convertBasicTokenInfoToCovalentBalanceItem(_params);
    return _convertedAsset;
  };
  const form = useForm({
    resolver: zodResolver(TradeAssetsPageSchema),
    mode: 'onChange',
    defaultValues: {
      tokenAmount: '0',
      usdAmount: '0',
      fromToken: undefined,
      toToken: null
    }
  });
  const [_formWatch, _watchUsdAmount, _watchFromToken, _watchToToken] = [form.watch(), form.watch('usdAmount'), form.watch('fromToken'), form.watch('toToken')];
  // find user balance if tokenData
  const _assetBalance = useMemo(() => {
    if (!assetData || !tokenData) return undefined;
    return assetData.balances.find(token => {
      var _a, _b, _c, _d;
      return (((_a = token.contract_address) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === ((_c = (_b = tokenData === null || tokenData === void 0 ? void 0 : tokenData.token) === null || _b === void 0 ? void 0 : _b.address) === null || _c === void 0 ? void 0 : _c.toLocaleLowerCase()) || ((_d = token.contract_address) === null || _d === void 0 ? void 0 : _d.toLocaleLowerCase()) === (tokenAddress === null || tokenAddress === void 0 ? void 0 : tokenAddress.toLocaleLowerCase())) && token.network === parseInt(tokenNetwork);
    });
  }, [assetData, tokenData]);
  // find to token user balance if any.
  const _toTokenBalance = useMemo(() => {
    if (!assetData || !_watchToToken) return undefined;
    return assetData.balances.find(token => {
      var _a, _b;
      return ((_a = token.contract_address) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === ((_b = _watchToToken === null || _watchToToken === void 0 ? void 0 : _watchToToken.contract_address) === null || _b === void 0 ? void 0 : _b.toLocaleLowerCase());
    });
  }, [_watchToToken]);
  const handleOnSwitchTokenSides = () => {
    form.setValue('usdAmount', '0');
    form.setValue('tokenAmount', '0');
    if (_watchFromToken !== _watchToToken) {
      form.setValue('fromToken', _watchToToken);
    } else {
      form.setValue('fromToken', _watchFromToken);
    }
    _watchToToken !== _watchFromToken ? form.setValue('toToken', _watchFromToken) : form.setValue('toToken', _watchToToken);
    return;
  };
  // on confirm stage, set swap assets
  // to reach confirm stage, the form must be already valid
  useEffect(() => {
    if (_watchFromToken && _watchUsdAmount) {
      setFromAssets([Object.assign(Object.assign({}, _watchFromToken), {
        amountToUseStr: _watchUsdAmount,
        amountToUse: Number(_watchUsdAmount)
      })]);
    }
    if (_watchToToken) {
      setToAssets([_watchToToken]);
    }
    return () => {};
  }, [_watchFromToken, _watchUsdAmount, _watchToToken]);
  // on balance change, update it
  useEffect(() => {
    var _a, _b, _c, _d;
    if (_assetBalance && ((_a = _watchFromToken === null || _watchFromToken === void 0 ? void 0 : _watchFromToken.contract_address) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === ((_b = _assetBalance === null || _assetBalance === void 0 ? void 0 : _assetBalance.contract_address) === null || _b === void 0 ? void 0 : _b.toLocaleLowerCase())) form.setValue('fromToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
    if (_toTokenBalance && ((_c = _watchToToken === null || _watchToToken === void 0 ? void 0 : _watchToToken.contract_address) === null || _c === void 0 ? void 0 : _c.toLocaleLowerCase()) === ((_d = _toTokenBalance === null || _toTokenBalance === void 0 ? void 0 : _toTokenBalance.contract_address) === null || _d === void 0 ? void 0 : _d.toLocaleLowerCase())) console.log('setting to token 2', _assetBalance, _convertTokenData(), tokenData, _watchToToken, _toTokenBalance);
    form.setValue('toToken', _toTokenBalance);
    return;
  }, [_assetBalance, _toTokenBalance]);
  useEffect(() => {
    var _a, _b;
    if (tokenAddress && tokenNetwork && tokenSide) {
      if (tokenSide === 'from' && tokenData && ((_a = _watchToToken === null || _watchToToken === void 0 ? void 0 : _watchToToken.contract_address) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) !== (tokenAddress === null || tokenAddress === void 0 ? void 0 : tokenAddress.toLocaleLowerCase())) {
        console.log('setting to token 3 fromtoken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        form.setValue('fromToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        return;
      }
      if (tokenSide === 'to' && tokenData && ((_b = _watchFromToken === null || _watchFromToken === void 0 ? void 0 : _watchFromToken.contract_address) === null || _b === void 0 ? void 0 : _b.toLocaleLowerCase()) !== (tokenAddress === null || tokenAddress === void 0 ? void 0 : tokenAddress.toLocaleLowerCase())) {
        console.log('setting to token 3', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        form.setValue('toToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        return;
      }
    }
    return () => {};
  }, [tokenData, tokenAddress, tokenNetwork, tokenSide]);
  return {
    form,
    refetchOrderHistory,
    setMode,
    setKeyword,
    stage,
    setStage,
    assetData,
    handleOnSwitchTokenSides
  };
};